/**
 * Reset Password Page for AdCanvas
 *
 * Allows users to set a new password after clicking the reset link.
 * Supabase handles the token automatically via the URL hash.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Lock, CheckCircle2, ArrowLeft } from 'lucide-react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  // Why the link failed, when we can tell. Without this every failure collapsed
  // into the same "invalid or expired" card and there was no way to distinguish
  // a genuinely stale token from a link opened in the wrong browser.
  const [linkError, setLinkError] = useState<string | null>(null);

  // Check for recovery token in URL and wait for session
  useEffect(() => {
    let isMounted = true;

    // Listen for auth state changes - this catches the PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      console.log('ResetPasswordPage: Auth event:', event, 'Session:', !!session);

      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (session) {
          setHasValidSession(true);
          setIsCheckingSession(false);
        }
      }
    });

    const handleRecoverySession = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

        // Supabase reports a refused link as params on the redirect itself
        // (error_code=otp_expired, access_denied, ...). Surface that verbatim
        // rather than guessing.
        const errorCode = hashParams.get('error_code') || urlParams.get('error_code');
        const errorDescription =
          hashParams.get('error_description') || urlParams.get('error_description');
        if (errorCode) {
          console.error('ResetPasswordPage: Link rejected:', errorCode, errorDescription);
          if (isMounted) {
            setLinkError(errorDescription?.replace(/\+/g, ' ') || errorCode);
            setIsCheckingSession(false);
          }
          return;
        }

        // Check for PKCE flow: code in query params. Links minted before the
        // switch to implicit flow still arrive this way, so keep handling them.
        const code = urlParams.get('code');

        if (code) {
          console.log('ResetPasswordPage: Found PKCE code, exchanging for session...');
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('ResetPasswordPage: Error exchanging code:', error);
            if (isMounted) {
              setLinkError(
                'This reset link was opened in a different browser than the one that requested it. Request a new link and open it in the same browser, or ask your administrator for a fresh invite.',
              );
            }
          } else if (data.session && isMounted) {
            console.log('ResetPasswordPage: PKCE exchange successful');
            setHasValidSession(true);
            setIsCheckingSession(false);
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        }

        // Check for implicit flow: tokens in hash (older Supabase)
        const hash = window.location.hash;
        if (hash.includes('access_token') || hash.includes('type=recovery')) {
          console.log('ResetPasswordPage: Found hash tokens, waiting for Supabase to process...');
          // Small delay to let Supabase process the URL hash
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        if (!isMounted) return;

        // Check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        if (session && isMounted) {
          console.log('ResetPasswordPage: Found existing session');
          setHasValidSession(true);
        }
      } catch (err) {
        console.error('Error checking session:', err);
      } finally {
        if (isMounted) {
          setIsCheckingSession(false);
        }
      }
    };

    handleRecoverySession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      console.log('ResetPasswordPage: Attempting password update...');
      const { error: updateError } = await updatePassword(password);

      if (updateError) {
        console.error('ResetPasswordPage: Password update error:', updateError);
        setError(updateError.message);
        setIsLoading(false);
        return;
      }

      console.log('ResetPasswordPage: Password updated successfully');
      setIsLoading(false);
      setIsSuccess(true);
    } catch (err) {
      console.error('ResetPasswordPage: Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Password updated
            </CardTitle>
            <CardDescription className="text-center">
              Your password has been successfully reset
            </CardDescription>
          </CardHeader>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Sign in with new password
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show loading while checking for recovery session
  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // No session - invalid or expired link
  if (!hasValidSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              Invalid or expired link
            </CardTitle>
            <CardDescription className="text-center">
              {linkError || 'This password reset link is invalid or has expired'}
            </CardDescription>
          </CardHeader>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              className="w-full"
              onClick={() => navigate('/forgot-password')}
            >
              Request a new reset link
            </Button>

            <Link
              to="/login"
              className="text-sm text-center text-primary hover:underline flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Set new password
          </CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update password'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
